<template>
  <div
    class="diagnostic-chart-element"
    :class="{ lock: elementInfo.lock, invisible: elementInfo.isInvisible }"
    :data-element-id="elementInfo.id"
    :style="{
      width: `${elementInfo.width}px`,
      height: `${elementInfo.height}px`,
      left: `${elementInfo.left}px`,
      top: `${elementInfo.top}px`,
    }"
  >
    <div class="rotate-wrapper" :style="{ transform: `rotate(${elementInfo.rotate}deg)` }">
      <div
        class="element-content"
        ref="elementRef"
        v-contextmenu="contextmenus || (() => null)"
        @mousedown="handleSelectElement"
        @touchstart="handleSelectElement"
        @dragstart.prevent
        @selectstart.prevent
      >
        <ElementOutline
          :width="elementInfo.width"
          :height="elementInfo.height"
        />
        <div
          v-if="isEditMode && currentChartImage"
          class="chart-image"
          :style="{ backgroundImage: `url('${currentChartImage}')` }"
        ></div>
        <DiagnosticChart
          v-else-if="elementInfo._snapshot?.series?.length"
          :series="elementInfo._snapshot.series"
        />
        <div v-else class="mock-chart"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ContextmenuItem } from '@/components/Contextmenu/types'
import DiagnosticChart from '@/components/DiagnosticChart/DiagnosticChart.vue'
import ElementOutline from '@/views/components/element/ElementOutline.vue'
import { useSlidesStore } from '@/store/slides'
import type { PPTDiagnosticChartElement, PPTElement } from '@/types/slides'

const props = defineProps<{
  elementInfo: PPTDiagnosticChartElement
  selectElement?: (event: MouseEvent | TouchEvent, element: PPTElement, canMove?: boolean) => void
  contextmenus?: () => ContextmenuItem[] | null
}>()

const slidesStore = useSlidesStore()
const isEditMode = computed(() =>
  typeof window !== 'undefined' &&
  window.parent !== window &&
  new URLSearchParams(window.location.search).get('mode') === 'report'
)
const currentChartImage = computed(() => {
  const image = slidesStore.chartImages?.[props.elementInfo.id]
  return typeof image === 'string' && image.startsWith('data:image') ? image : null
})

const handleSelectElement = (event: MouseEvent | TouchEvent) => {
  if (props.elementInfo.lock) return
  event.stopPropagation()
  event.preventDefault()
  props.selectElement?.(event, props.elementInfo)
}
</script>

<style scoped>
.diagnostic-chart-element {
  position: absolute;
  cursor: pointer;
  user-select: none;
  pointer-events: auto;
}

.rotate-wrapper,
.element-content,
.chart-image {
  width: 100%;
  height: 100%;
}

.element-content {
  position: relative;
}

.lock,
.lock .element-content {
  cursor: default;
}

.invisible {
  opacity: 0;
}

.chart-image {
  position: absolute;
  inset: 0;
  z-index: 1001;
  background: white center / contain no-repeat;
}

.mock-chart {
  width: 100%;
  height: 100%;
  background: white url('/images/mock-chart-diagnostic.svg') center / 100% 100% no-repeat;
}
</style>
